import { RouteComponentProps } from "@reach/router";
import { FunctionComponent, useContext, useEffect, useState } from "react";
import Button from "../components/button/Button";
import SecretDisplay from "../components/secret-display/SecretDisplay";
import LayoutContext from "../context/layout-context";
import UserContext from "../context/user-context";
import {
  getIssuerWithUsername,
  updateIssuerSecret
} from "../utils/helpers/data/issuers";
import { Issuer } from "../utils/helpers/types";
import { resources } from "../utils/resources";
import { AddEditIssuerModal } from "./issuers";
import styles from "./issuers.module.scss";

const roleRender: (value: string) => JSX.Element = value => (
  <span className="tag smaller">{value.replace("_", " ").toUpperCase()}</span>
);

const ProfilePage: FunctionComponent<RouteComponentProps> = () => {
  const [issuer, setIssuer] = useState<Issuer | null>(null);
  const [editingIssuer, setEditingIssuer] = useState(false);
  const [loading, setLoading] = useState(false);

  const { username } = useContext(UserContext) || {};
  const { notify, confirm } = useContext(LayoutContext);

  const fetchIssuer = async (username: string) => {
    setLoading(true);
    const response = await getIssuerWithUsername(username);
    setLoading(false);
    if (response.error) {
      notify("error", `Unable to fetch issuer details: ${response.message}`);
      return;
    }
    setIssuer(response.data);
  };

  useEffect(() => {
    if (username) {
      fetchIssuer(username);
    }
  }, [username]);

  const updateSecret = async () => {
    const response = await updateIssuerSecret(issuer?.issuerId as string);
    if (response.error) {
      notify("error", `Unable to regenerate secret: ${response.message}`);
      return;
    }
    notify("success", "Secret generated successfully");
    fetchIssuer(username as string);
  };

  const handleSecretRegeneration = () => {
    confirm({
      title: "Are you sure you want to regenerate your secret key?",
      body: "Current secret key would no longer be valid",
      onOk: updateSecret
    });
  };

  if (!username) {
    return <h1 className="danger">Issuer Not found</h1>;
  }

  return (
    <main className="content-layout">
      <h1 className="title vertical-margin spaced thin">Issuer Profile</h1>
      <div className={[styles["issuer-view-wrapper"], styles.padded].join(" ")}>
        <div className={styles["key-value"]}>
          <strong className={styles.key}>Row ID:</strong>
          <span className={styles.value}>{issuer?.id}</span>
        </div>
        <div className={styles["key-value"]}>
          <strong className={styles.key}>Issuer ID:</strong>
          <span className={styles.value}>{issuer?.issuerId}</span>
        </div>
        <div className={styles["key-value"]}>
          <strong className={styles.key}>Username:</strong>
          <span className={styles.value}>{issuer?.username}</span>
        </div>
        <div className={styles["key-value"]}>
          <strong className={styles.key}>User Role:</strong>
          <span className={styles.value}>
            {roleRender(issuer?.userRole || "")}
          </span>
        </div>
        <div className={styles["key-value"]}>
          <strong className={styles.key}>Organization:</strong>
          <span className={styles.value}>{issuer?.organizationName}</span>
        </div>
        <div className={styles["key-value"]}>
          <strong className={styles.key}>Host Domain:</strong>
          <span className={styles.value}>{issuer?.hostDomain}</span>
        </div>
        <div className={styles["key-value"]}>
          <strong className={styles.key}>API Path:</strong>
          <span className={styles.value}>{issuer?.apiPath}</span>
        </div>
        <div className={styles["key-value"]}>
          <strong className={styles.key}>Auth Token:</strong>
          {issuer && (
            <SecretDisplay
              value={issuer?.basicAuthToken || ""}
              className={styles.value}
            />
          )}
        </div>
        <div className={styles["key-value"]}>
          <strong className={styles.key}>Secret Key:</strong>
          {issuer && (
            <SecretDisplay
              value={issuer?.secretKey || ""}
              className={styles.value}
            />
          )}
        </div>

        <br />
        {issuer && (
          <div className="flex spaced margin-top">
            <Button
              danger
              type="accent"
              id="regenerateSecretButton"
              onClick={handleSecretRegeneration}
            >
              Regenerate Secret
            </Button>
            <Button
              className="editProfileButton"
              onClick={() => setEditingIssuer(true)}
            >
              Edit Profile
            </Button>
          </div>
        )}

        <div className={[styles.loader, loading && styles.active].join(" ")}>
          <img
            src={resources.images.ANIMATION_IMG_SPIN}
            className={styles.image}
            alt="loading"
          />
        </div>
      </div>

      <AddEditIssuerModal
        visible={editingIssuer}
        record={issuer}
        cancel={() => setEditingIssuer(false)}
        refresh={() => fetchIssuer(username as string)}
      />
    </main>
  );
};

export default ProfilePage;
